import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, MapPin, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { alertDisplay, alertTypeLabel } from '@/lib/alerts';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { relativeTime } from '@logistech/ui-shared';
import { requirePermission } from '@/lib/route-guards';
import { cn } from '@/lib/utils';

import type { Alert, Paginated, Vehicle, VehicleState } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/alerts')({
  beforeLoad: () => requirePermission('alerts.read'),
  component: AlertsPage,
});

const SEVERITY_LABELS: Record<Alert['severity'], string> = { CRITICAL: 'Critique', WARNING: 'Avertissement', INFO: 'Info' };
const SEVERITY_VARIANT = { CRITICAL: 'destructive', WARNING: 'warning', INFO: 'default' } as const;
const STATUS_LABELS: Record<Alert['status'], string> = {
  OPEN: 'Ouverte', ACKNOWLEDGED: 'Acquittée', RESOLVED: 'Résolue', DISMISSED: 'Ignorée',
};

const ALL = 'all';
const fmt = (d: string | null) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const coords = (lat: number, lon: number) => `${lat.toFixed(5)}, ${lon.toFixed(5)}`;

/**
 * Lieu de l'alerte (context.occurredAtPosition, posé par alerts-service). Sans fix GPS (SOS boîtier en zone
 * blanche…), lat / lon sont null : repli sur la dernière position connue du véhicule (VehicleState).
 */
export const alertPositionLabel = (a: Pick<Alert, 'context'>, state: VehicleState | undefined): string => {
  const p = (a.context?.['occurredAtPosition'] ?? null) as { lat?: number | null; lon?: number | null } | null;
  if (typeof p?.lat === 'number' && typeof p.lon === 'number') return `Position : ${coords(p.lat, p.lon)}`;
  if (state?.lastLat != null && state.lastLon != null) {
    const seen = state.lastTime ? `, ${relativeTime(state.lastTime)}` : '';
    return `Position inconnue (dernière position connue : ${coords(state.lastLat, state.lastLon)}${seen})`;
  }
  return 'Position non disponible pour ce véhicule';
};

function AlertsPage() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">Alertes</h1>
      <Tabs defaultValue="current">
        <TabsList>
          <TabsTrigger value="current">En cours</TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="current"><CurrentAlerts /></TabsContent>
        <TabsContent value="history"><AlertHistory /></TabsContent>
      </Tabs>
    </div>
  );
}

/** Alertes à traiter (ouvertes / acquittées), rafraîchies en continu. */
function CurrentAlerts() {
  const qc = useQueryClient();
  const canWrite = useHasPermission('alerts.write');
  const canReadVehicles = useHasPermission('vehicles.read');
  const [severity, setSeverity] = useState(ALL);
  // Dernière position connue : repli des alertes sans coordonnées (même source que la carte live)
  const { data: states } = useQuery({
    queryKey: ['positions', 'states'],
    queryFn: async () => (await api.get<{ data: VehicleState[] }>('/positions/states')).data,
    enabled: canReadVehicles,
    refetchInterval: 60_000,
  });
  const stateOf = (vehicleId: string | null) =>
    vehicleId ? states?.data?.find((s) => s.vehicleId === vehicleId) : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', 'current', severity],
    queryFn: async () => {
      const params = new URLSearchParams({ status: 'OPEN,ACKNOWLEDGED', limit: '200' });
      if (severity !== ALL) params.set('severity', severity);
      return (await api.get<Paginated<Alert>>(`/alerts?${params.toString()}`)).data;
    },
    refetchInterval: 15_000,
  });

  const act = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'ack' | 'resolve' }) => api.post(`/alerts/${id}/${kind}`),
    onSuccess: (_r, { kind }) => {
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      toast.success(kind === 'ack' ? 'Alerte acquittée' : 'Alerte résolue');
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Action impossible')),
  });

  return (
    <div className="space-y-3 pt-2">
      <div className="flex justify-end">
        <SeveritySelect value={severity} onChange={setSeverity} />
      </div>
      {isLoading && Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
      {data?.data?.map((a) => {
        const { title, detail } = alertDisplay(a);
        return (
          <div
            key={a.id}
            className={cn(
              'bg-card border rounded-lg p-4 flex items-start gap-4 border-l-4',
              a.severity === 'CRITICAL' ? 'border-l-danger' : a.severity === 'WARNING' ? 'border-l-warning' : 'border-l-primary-500',
            )}
          >
            <AlertTriangle className={cn('h-5 w-5 mt-0.5 shrink-0',
              a.severity === 'CRITICAL' ? 'text-danger' : a.severity === 'WARNING' ? 'text-warning' : 'text-primary-500',
            )} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{title}</span>
                <Badge variant={SEVERITY_VARIANT[a.severity]}>{SEVERITY_LABELS[a.severity]}</Badge>
                <Badge variant="outline">{alertTypeLabel(a.type)}</Badge>
                <Badge variant="secondary">{STATUS_LABELS[a.status]}</Badge>
              </div>
              {detail && <p className="text-sm text-muted-foreground mt-1">{detail}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                {relativeTime(a.openedAt)}
                {a.vehicle && ` · ${a.vehicle.plate}`}
                {a.driver && ` · ${a.driver.firstName} ${a.driver.lastName}`}
                {a.status === 'ACKNOWLEDGED' && a.ackByName && ` · acquittée par ${a.ackByName}`}
              </p>
              {a.vehicleId && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3 shrink-0" /> {alertPositionLabel(a, stateOf(a.vehicleId))}
                </p>
              )}
            </div>
            {canWrite && (
              <div className="flex gap-2 shrink-0">
                {a.status === 'OPEN' && (
                  <Button size="sm" variant="outline" onClick={() => act.mutate({ id: a.id, kind: 'ack' })} disabled={act.isPending}>
                    <CheckCircle className="h-4 w-4 mr-1" /> Acquitter
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => act.mutate({ id: a.id, kind: 'resolve' })} disabled={act.isPending}>
                  <XCircle className="h-4 w-4 mr-1" /> Résoudre
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {data && (data.data?.length ?? 0) === 0 && (
        <div className="text-center text-muted-foreground py-12">Aucune alerte en cours</div>
      )}
    </div>
  );
}

/** Alertes clôturées (résolues / ignorées) sur une période, avec qui a traité et quand. */
function AlertHistory() {
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [vehicleId, setVehicleId] = useState(ALL);
  const [severity, setSeverity] = useState(ALL);

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: async () => (await api.get<Paginated<Vehicle>>('/vehicles?pageSize=100')).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', 'history', from, to, vehicleId, severity],
    queryFn: async () => {
      const params = new URLSearchParams({
        status: 'RESOLVED,DISMISSED',
        limit: '200',
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
      });
      if (vehicleId !== ALL) params.set('vehicleId', vehicleId);
      if (severity !== ALL) params.set('severity', severity);
      return (await api.get<Paginated<Alert>>(`/alerts?${params.toString()}`)).data;
    },
    enabled: !!from && !!to,
  });

  return (
    <div className="space-y-3 pt-2">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="h-from">Du</Label>
          <Input id="h-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="h-to">Au</Label>
          <Input id="h-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <Label>Véhicule</Label>
          <Select value={vehicleId} onValueChange={setVehicleId}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les véhicules</SelectItem>
              {vehicles?.data?.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Gravité</Label>
          <SeveritySelect value={severity} onChange={setSeverity} />
        </div>
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Déclenchée le</TableHead>
              <TableHead>Alerte</TableHead>
              <TableHead>Véhicule / conducteur</TableHead>
              <TableHead>Gravité</TableHead>
              <TableHead>Acquittée</TableHead>
              <TableHead>Clôture</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {data?.data?.map((a) => {
              const { title, detail } = alertDisplay(a);
              return (
                <TableRow key={a.id}>
                  <TableCell className="whitespace-nowrap">{fmt(a.openedAt)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{title}</div>
                    <div className="text-xs text-muted-foreground">{alertTypeLabel(a.type)}{detail && ` · ${detail}`}</div>
                  </TableCell>
                  <TableCell>
                    {a.vehicle?.plate ?? '—'}
                    {a.driver && <div className="text-xs text-muted-foreground">{a.driver.firstName} {a.driver.lastName}</div>}
                  </TableCell>
                  <TableCell><Badge variant={SEVERITY_VARIANT[a.severity]}>{SEVERITY_LABELS[a.severity]}</Badge></TableCell>
                  <TableCell className="text-xs">
                    {a.ackAt ? <>{fmt(a.ackAt)}{a.ackByName && <div className="text-muted-foreground">par {a.ackByName}</div>}</> : '—'}
                  </TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="secondary">{STATUS_LABELS[a.status]}</Badge>
                    <div className="mt-1">{fmt(a.resolvedAt)}</div>
                    {a.resolvedByName && <div className="text-muted-foreground">par {a.resolvedByName}</div>}
                    {a.status === 'RESOLVED' && !a.resolvedByName && a.resolvedAt && (
                      <div className="text-muted-foreground">automatique</div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {data && (data.data?.length ?? 0) === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucune alerte clôturée sur cette période</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {data && data.total > data.data.length && (
        <p className="text-xs text-muted-foreground">{data.data.length} plus récentes affichées sur {data.total} — affinez la période.</p>
      )}
    </div>
  );
}

function SeveritySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>Toutes gravités</SelectItem>
        <SelectItem value="CRITICAL">Critique</SelectItem>
        <SelectItem value="WARNING">Avertissement</SelectItem>
        <SelectItem value="INFO">Info</SelectItem>
      </SelectContent>
    </Select>
  );
}
